import React, { useEffect, useMemo, useState } from 'react';
import './reaction.css';

const HOLES = Array.from({ length: 9 }, (_, index) => index + 1);

export default function ReactionPlayer({ socket, sessionId, playerName }) {
  const [state, setState] = useState({
    phase: 'lobby',
    round: 1,
    totalRounds: 3,
    roundName: 'Classic Rush',
    briefingEndsAt: null,
    roundEndsAt: null,
    summaryEndsAt: null,
    avatarChoices: [],
    scoreboard: [],
  });

  const [timeLeftMs, setTimeLeftMs] = useState(0);
  const [feedback, setFeedback] = useState('Watch the TV and tap the matching hole.');
  const [feedbackTone, setFeedbackTone] = useState('neutral');
  const [tapBursts, setTapBursts] = useState([]);

  const pushBurst = (holeIndex, delta, tone) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const label = `${delta >= 0 ? '+' : ''}${delta}`;
    setTapBursts((prev) => [...prev, { id, holeIndex, delta, tone, label }]);
    setTimeout(() => {
      setTapBursts((prev) => prev.filter((item) => item.id !== id));
    }, 680);
  };

  useEffect(() => {
    if (!socket) return undefined;

    const onState = (incoming) => setState((prev) => ({ ...prev, ...incoming }));

    const onAck = (payload) => {
      if (payload.status === 'mole-hit') {
        setFeedback(`${payload.tie ? 'Tie hit' : 'Mole hit'} • ${payload.delta >= 0 ? '+' : ''}${payload.delta}`);
        setFeedbackTone(payload.delta > 0 ? 'good' : 'neutral');
        pushBurst(payload.holeIndex, payload.delta, payload.delta > 0 ? 'good' : 'neutral');
        return;
      }
      if (payload.status === 'mole-miss') {
        setFeedback('Missed • +0');
        setFeedbackTone('neutral');
        pushBurst(payload.holeIndex, 0, 'neutral');
        return;
      }
      if (payload.status === 'bomb') {
        setFeedback(`Bomb hit • ${payload.delta}`);
        setFeedbackTone('bad');
        pushBurst(payload.holeIndex, payload.delta, 'bad');
        return;
      }
      if (payload.status === 'empty') {
        setFeedback('Empty hole • +0');
        setFeedbackTone('neutral');
        pushBurst(payload.holeIndex, 0, 'neutral');
        return;
      }
      if (payload.status === 'pending') {
        setFeedback('Tap registered...');
        setFeedbackTone('neutral');
      }
    };

    socket.on('reactionState', onState);
    socket.on('reactionTapAck', onAck);
    socket.emit('reactionSyncState', { sessionId });

    return () => {
      socket.off('reactionState', onState);
      socket.off('reactionTapAck', onAck);
    };
  }, [socket, sessionId]);

  useEffect(() => {
    const targetEndsAt = state.phase === 'round-briefing'
      ? state.briefingEndsAt
      : state.phase === 'active-round'
        ? state.roundEndsAt
        : state.phase === 'round-summary'
          ? state.summaryEndsAt
          : null;

    if (!targetEndsAt) {
      setTimeLeftMs(0);
      return undefined;
    }

    const tick = () => {
      setTimeLeftMs(Math.max(targetEndsAt - Date.now(), 0));
    };

    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [state.phase, state.briefingEndsAt, state.roundEndsAt, state.summaryEndsAt]);

  const me = useMemo(
    () => (state.scoreboard || []).find((entry) => entry.name === playerName),
    [state.scoreboard, playerName],
  );

  const leaderboardLocked = state.round === 3 && state.phase !== 'game-over';

  const tapHole = (holeIndex) => {
    if (state.phase !== 'active-round') return;
    socket.emit('reactionTap', {
      sessionId,
      holeIndex,
      timestamp: Date.now(),
    });
  };

  const chooseAvatar = (avatar) => {
    socket.emit('reactionSelectAvatar', {
      sessionId,
      avatar,
    });
  };

  return (
    <div className="rr-player-shell">
      <div className="rr-player-card">
        <div className="rr-phone-top-row">
          <div>Round {state.round}/{state.totalRounds}</div>
          <div>{playerName} {me?.avatar || ''} • {me?.points ?? 0}</div>
        </div>

        <h2>Reaction Rush</h2>
        <p>{state.roundName}</p>

        {state.phase === 'lobby' && (
          <div className="rr-avatar-picker">
            <h4>Choose Avatar</h4>
            <div className="rr-avatar-grid">
              {(state.avatarChoices || []).map((avatar) => (
                <button
                  key={avatar}
                  className={`rr-avatar-choice ${me?.avatar === avatar ? 'selected' : ''}`}
                  onClick={() => chooseAvatar(avatar)}
                >
                  {avatar}
                </button>
              ))}
            </div>
            <p>Waiting for host to start.</p>
          </div>
        )}

        {(state.phase === 'round-briefing' || state.phase === 'round-summary') && (
          <div className="rr-phone-status">
            <p>{state.phase === 'round-briefing' ? 'Get ready...' : 'Round complete...'}</p>
            <p>{(timeLeftMs / 1000).toFixed(1)}s</p>
          </div>
        )}

        <div className="rr-phone-hole-grid">
          {HOLES.map((holeIndex) => (
            <button
              key={holeIndex}
              className="rr-phone-hole"
              onClick={() => tapHole(holeIndex)}
              disabled={state.phase !== 'active-round'}
            >
              <span className="rr-phone-hole-number">{holeIndex}</span>
              <span className="rr-phone-hole-burst-layer">
                {tapBursts.filter((burst) => burst.holeIndex === holeIndex).map((burst) => (
                  <span key={burst.id} className={`rr-tap-burst ${burst.tone}`}>
                    <span className="rr-tap-poof" />
                    <span className="rr-tap-text">{burst.label}</span>
                  </span>
                ))}
              </span>
            </button>
          ))}
        </div>

        {state.phase === 'active-round' && (
          <p className="rr-player-timer">{(timeLeftMs / 1000).toFixed(1)}s</p>
        )}

        <div className={`rr-player-feedback ${feedbackTone}`}>{feedback}</div>

        <div className="rr-player-mini-board">
          {leaderboardLocked ? (
            <>
              <h4>Leaderboard Locked</h4>
              <p>Round 3 scores are hidden until the final reveal.</p>
            </>
          ) : (
            <>
              <h4>Top Scores</h4>
              {[...(state.scoreboard || [])].sort((a, b) => b.points - a.points).slice(0, 6).map((entry, index) => (
                <div key={entry.id} className="rr-player-mini-row">
                  <span>#{index + 1} {entry.avatar} {entry.name}</span>
                  <strong>{entry.points}</strong>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
